/**
 * PaymentProvidersListController — page /parametres-encaissement.html
 * (EPIC-042 / 42.6).
 *
 * Hydrate la liste des prestataires d'encaissement « autres » de l'org
 * (§7.I.H) depuis `GET /v1/payment-providers` (qui inclut les
 * archivés depuis le delta 42.6 du repo). Permet la désactivation et la
 * réactivation depuis le tableau, sans rechargement de page.
 *
 * États d'une ligne (cf. décisions tranchées 42.6) :
 *   - Actif        (is_active=true,  archived_at=NULL) → badge --olive  · Modifier + Désactiver
 *   - Inactif      (is_active=false, archived_at=NULL) → badge neutre   · Modifier + Réactiver
 *   - Archivé      (is_active=false, archived_at!=NULL)→ badge neutre   · Réactiver
 *                                                       + classe `is-archived` sur le <tr>
 *
 * 422 `payment_provider_has_recent_transactions` sur deactivate :
 *   - Le backend a bien désactivé le provider (la 422 signale juste que
 *     l'archivage est refusé). On affiche le message d'erreur dédié dans
 *     `errorBanner` et on recharge la liste pour refléter l'état Inactif.
 *
 * Convention CLAUDE.md respectée : pas d'HTML inline en JS (clone du
 * `<template id="provider-row-tpl">` + remplissage data-slot). Aucune
 * classe CSS inventée — `badge`, `badge--olive`, `is-archived`, `is-mono`,
 * `nm-btn`, `nm-btn--sm`, `nm-btn--danger` sont toutes définies dans
 * notamicro.css (copie bit-à-bit de maquettes/shared/styles.css).
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const DATE_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

export default class PaymentProvidersListController extends BaseController {
  static targets = ["tableBody", "errorBanner"];

  connect() {
    this._fetchSeq = 0;
    this.refresh();
  }

  // ---------------------------------------------------------------------------
  // Fetch + render
  // ---------------------------------------------------------------------------

  async refresh() {
    const seq = ++this._fetchSeq;
    this._hideErrorBanner();
    try {
      const [list, catalog] = await Promise.all([
        api.paymentProviders.list(),
        api.paymentProviders.catalog().catch(() => []),
      ]);
      if (seq !== this._fetchSeq) return;
      const instances = Array.isArray(list) ? list : [];
      const catalogById = new Map(
        (Array.isArray(catalog) ? catalog : []).map((p) => [p.id, p]),
      );
      this._renderRows(instances, catalogById);
    } catch (error) {
      if (seq !== this._fetchSeq) return;
      this._showErrorBanner(
        error instanceof APIError && error.message
          ? error.message
          : "Impossible de charger les moyens de paiement.",
      );
    }
  }

  _renderRows(instances, catalogById) {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();
    for (const instance of instances) {
      const catalog = catalogById.get(instance.paymentProviderId) ?? null;
      const fragment = this._buildRow(instance, catalog);
      if (fragment) this.tableBodyTarget.appendChild(fragment);
    }
  }

  _buildRow(instance, catalog) {
    const fragment = this.cloneTemplate("provider-row-tpl");
    if (!fragment) return null;

    const isArchived = Boolean(instance.archivedAt);
    const isActive = instance.isActive === true && !isArchived;
    const state = isArchived ? "archived" : isActive ? "active" : "inactive";

    this.fillSlots(fragment, {
      label: instance.label ?? catalog?.label ?? "—",
      code: catalog?.label ?? "—",
      lastSyncAt: this._formatLastSync(instance.lastSyncAt),
    });

    const statusCell = fragment.querySelector('[data-slot="status"]');
    if (statusCell) {
      statusCell.appendChild(this._buildStatusBadge(state));
    }

    const lastSyncCell = fragment.querySelector('[data-slot="lastSyncAt"]');
    if (lastSyncCell && !instance.lastSyncAt) {
      // Aligné sur la maquette : « Jamais » et « — » sont des is-muted.
      lastSyncCell.classList.add("is-muted");
    }

    const actionsCell = fragment.querySelector('[data-slot="actions"]');
    if (actionsCell) {
      this._fillActions(actionsCell, instance, state);
    }

    const tr = fragment.querySelector("tr");
    if (tr && isArchived) {
      tr.classList.add("is-archived");
    }

    return fragment;
  }

  _buildStatusBadge(state) {
    const span = document.createElement("span");
    span.classList.add("badge");
    if (state === "active") {
      span.classList.add("badge--olive");
      span.textContent = "Actif";
    } else if (state === "inactive") {
      span.textContent = "Inactif";
    } else {
      span.textContent = "Archivé";
    }
    return span;
  }

  _fillActions(cell, provider, state) {
    // Lit dans cet ordre : Modifier (si non-archivé), Désactiver (si actif),
    // Réactiver (si inactif ou archivé). Cohérent avec la maquette.
    const id = provider.id;
    if (state !== "archived") {
      const editLink = document.createElement("a");
      editLink.classList.add("nm-btn", "nm-btn--sm");
      editLink.textContent = "Modifier";
      editLink.href = `/parametres-encaissement-edition.html?id=${encodeURIComponent(id)}`;
      cell.appendChild(editLink);
    }
    if (state === "active") {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.classList.add("nm-btn", "nm-btn--sm", "nm-btn--danger");
      btn.textContent = "Désactiver";
      btn.dataset.providerId = id;
      btn.dataset.action = "click->payment-providers-list#deactivate";
      cell.appendChild(btn);
    } else {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.classList.add("nm-btn", "nm-btn--sm");
      btn.textContent = "Réactiver";
      btn.dataset.providerId = id;
      btn.dataset.action = "click->payment-providers-list#reactivate";
      cell.appendChild(btn);
    }
  }

  _clearTableBody() {
    while (this.tableBodyTarget.firstChild) {
      this.tableBodyTarget.removeChild(this.tableBodyTarget.firstChild);
    }
  }

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  async deactivate(event) {
    const button = event.currentTarget;
    const id = button?.dataset?.providerId;
    if (!id) return;
    button.disabled = true;
    this._hideErrorBanner();
    try {
      await api.paymentProviders.deactivate(id);
      await this.refresh();
    } catch (error) {
      // 422 has_recent_transactions : provider tout de même désactivé côté
      // backend → on recharge pour refléter l'état Inactif, puis on affiche
      // le message d'erreur dédié.
      if (
        error instanceof APIError
        && error.status === 422
        && error.data?.error?.code === "payment_provider_has_recent_transactions"
      ) {
        await this.refresh();
        this._showErrorBanner(
          error.data.error.message
            || "Ce moyen de paiement a des transactions récentes. "
              + "Il a été désactivé mais ne peut pas encore être archivé.",
        );
        return;
      }
      this._showErrorBanner(
        error instanceof APIError && error.message
          ? error.message
          : "Impossible de désactiver ce moyen de paiement.",
      );
      button.disabled = false;
    }
  }

  async reactivate(event) {
    const button = event.currentTarget;
    const id = button?.dataset?.providerId;
    if (!id) return;
    button.disabled = true;
    this._hideErrorBanner();
    try {
      await api.paymentProviders.reactivate(id);
      await this.refresh();
    } catch (error) {
      this._showErrorBanner(
        error instanceof APIError && error.message
          ? error.message
          : "Impossible de réactiver ce moyen de paiement.",
      );
      button.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _formatLastSync(value) {
    if (!value) return "Jamais";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return DATE_FORMATTER.format(date);
  }

  _showErrorBanner(message) {
    if (!this.hasErrorBannerTarget) return;
    this.errorBannerTarget.textContent = message;
    this.errorBannerTarget.hidden = false;
  }

  _hideErrorBanner() {
    if (!this.hasErrorBannerTarget) return;
    this.errorBannerTarget.hidden = true;
    this.errorBannerTarget.textContent = "";
  }
}
